import React from 'react';
import { 
  ShieldAlert, 
  X, 
  Phone, 
  Mail, 
  MessageCircle, 
  Building2, 
  Clock, 
  HelpCircle,
  ExternalLink
} from 'lucide-react';
import { 
  TRANSPORTADORA_CONFIG, 
  getWhatsAppSuporteUrl, 
  getEmailSuporteUrl 
} from '../../config/transportadoraConfig';

interface UsuarioInativoModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
  userName?: string;
  motivo?: string;
}

export const UsuarioInativoModal: React.FC<UsuarioInativoModalProps> = ({
  isOpen,
  onClose,
  userEmail,
  userName,
  motivo
}) => {
  if (!isOpen) return null;

  const whatsappUrl = getWhatsAppSuporteUrl(userEmail, userName);
  const emailUrl = getEmailSuporteUrl(userEmail, userName);

  return (
    <div 
      id="modal-usuario-inativo-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in"
    >
      <div 
        id="modal-usuario-inativo-card"
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-rose-100 overflow-hidden"
      >
        {/* Header Visual com Gradiente */}
        <div className="bg-gradient-to-br from-rose-600 via-rose-700 to-red-800 p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
            title="Fechar"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/20 shadow-inner">
              <ShieldAlert className="w-7 h-7 text-white" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-widest text-rose-200 bg-white/10 px-2 py-0.5 rounded-md">
                Status: Inativo
              </span>
              <h2 className="text-xl font-black text-white tracking-tight mt-0.5">
                Acesso Desativado
              </h2>
            </div>
          </div>
        </div>

        {/* Conteúdo Informativo */}
        <div className="p-6 space-y-5">
          <div className="bg-rose-50 border border-rose-200/80 rounded-2xl p-4">
            <h3 className="text-sm font-bold text-rose-900">
              Usuário Inativo no Sistema
            </h3>
            <p className="text-xs text-rose-800/90 mt-1.5 leading-relaxed">
              O seu cadastro de usuário encontra-se <strong>inativo</strong> no momento.
              Para restabelecer ou solicitar a reativação do seu acesso, <strong>por favor entre em contato com a transportadora</strong>.
            </p>
            {motivo && (
              <p className="text-xs text-rose-700 mt-2 pt-2 border-t border-rose-200 italic">
                Motivo/Detalhe: {motivo}
              </p>
            )}
            {userEmail && (
              <div className="mt-2 text-[11px] text-rose-700/80 font-medium">
                Conta associada: <span className="font-semibold underline">{userEmail}</span>
              </div>
            )}
          </div>

          {/* Seção: Canais de Atendimento da Transportadora */}
          <div>
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" />
              Canais da Transportadora
            </h4>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2.5 text-xs text-slate-700">
              <div className="font-bold text-slate-900 flex items-center justify-between">
                <span>{TRANSPORTADORA_CONFIG.nome}</span>
                <span className="text-[10px] text-slate-500 font-normal">{TRANSPORTADORA_CONFIG.nomeExibicao}</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t border-slate-200/70">
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-slate-400" />
                  <span>Central: <strong>{TRANSPORTADORA_CONFIG.telefoneFormatado}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  <span>{TRANSPORTADORA_CONFIG.emailContato}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-slate-500 pt-1 border-t border-slate-200/70">
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                <span>Horário: {TRANSPORTADORA_CONFIG.horarioAtendimento}</span>
              </div>
            </div>
          </div>

          {/* Botões de Ação para Contatar */}
          <div className="space-y-2.5 pt-2">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2.5 py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold shadow-lg shadow-emerald-600/25 transition active:scale-[0.98]"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Entrar em contato via WhatsApp</span>
              <ExternalLink className="w-3.5 h-3.5 ml-auto opacity-70" />
            </a>

            <a
              href={emailUrl}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold transition active:scale-[0.98]"
            >
              <Mail className="w-4 h-4" />
              <span>Enviar E-mail para Suporte</span>
            </a>

            <button
              type="button"
              onClick={onClose}
              className="w-full py-2.5 px-4 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-semibold transition"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
